import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MainLayout from "../../../components/layout/MainLayout";
import { useMasterData } from "../../../hooks/useMasterData";
import { usePermissions } from "../../../contexts/PermissionContext";
import { feesApi } from "../api/feesApi";

type StudentOption = {
  studentId: string;
  studentSessionRecordId: string;
  firstName: string;
  lastName: string;
  scholarNumber?: string;
  admissionNumber?: string;
  class?: { id: string; name: string };
  section?: { id: string; name: string };
  session?: { id: string; name: string };
};

type FeeHeadOption = {
  id: string;
  name: string;
};

type PaymentModeOption = {
  id: string;
  name: string;
  code: string;
  isOnline: boolean;
  requiresReference: boolean;
  isActive?: boolean;
};

const amount = (value: any) => Number(value || 0);

const FeeDepositPage: React.FC = () => {
  const { masters, fetchAcademicSessions, fetchSections, fetchMasters } = useMasterData();
  const { hasAnyPermission } = usePermissions();

  const [sessionId, setSessionId] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");

  const [sections, setSections] = useState<Array<{ id: string; name: string }>>([]);

  const [search, setSearch] = useState("");
  const [studentOptions, setStudentOptions] = useState<StudentOption[]>([]);
  const [student, setStudent] = useState<StudentOption | null>(null);

  const [demand, setDemand] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const [loadingDemand, setLoadingDemand] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);

  const [paymentMode, setPaymentMode] = useState("");
  const [paymentModes, setPaymentModes] = useState<PaymentModeOption[]>([]);
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [referenceNo, setReferenceNo] = useState("");
  const [remarks, setRemarks] = useState("");
  const [chequeNo, setChequeNo] = useState("");
  const [chequeBank, setChequeBank] = useState("");
  const [chequeDate, setChequeDate] = useState(new Date().toISOString().slice(0, 10));

  const [submitting, setSubmitting] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [discountType, setDiscountType] = useState("PERCENTAGE");
  const [discountValue, setDiscountValue] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [discountApplyToAll, setDiscountApplyToAll] = useState(true);
  const [discountFeeHeadIds, setDiscountFeeHeadIds] = useState<string[]>([]);

  const [fineType, setFineType] = useState("FIXED");
  const [fineValue, setFineValue] = useState("");
  const [fineReason, setFineReason] = useState("");
  const [fineApplyToAll, setFineApplyToAll] = useState(true);
  const [fineFeeHeadIds, setFineFeeHeadIds] = useState<string[]>([]);

  const canCreate = useMemo(
    () => hasAnyPermission(["FEES_DEPOSIT_CREATE", "FEES_DEPOSIT", "FEE_MANAGE"]),
    [hasAnyPermission],
  );

  const due = amount(summary?.finalDue ?? demand?.totalDue);
  const selectedPaymentMode = useMemo(
    () => paymentModes.find((m) => m.id === paymentMode),
    [paymentModes, paymentMode],
  );
  const isChequeMode = (selectedPaymentMode?.code || "").toUpperCase() === "CHEQUE";
  const isReferenceRequired = !!selectedPaymentMode?.requiresReference;

  const feeHeadOptions: FeeHeadOption[] = useMemo(() => {
    const map = new Map<string, string>();
    (demand?.breakdown || []).forEach((row: any) => {
      if (row?.feeHeadId && row?.feeHead) map.set(row.feeHeadId, row.feeHead);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [demand?.breakdown]);

  useEffect(() => {
    fetchMasters();
    fetchAcademicSessions();
  }, [fetchMasters, fetchAcademicSessions]);

  useEffect(() => {
    let cancelled = false;
    const loadPaymentModes = async () => {
      try {
        const rows = await feesApi.paymentModes({ isActive: true });
        const modes = Array.isArray(rows) ? rows : [];
        if (cancelled) return;
        setPaymentModes(modes);
        setPaymentMode((prev) => {
          if (prev && modes.some((m: PaymentModeOption) => m.id === prev)) return prev;
          return modes[0]?.id || "";
        });
      } catch {
        if (!cancelled) {
          setPaymentModes([]);
          setPaymentMode("");
        }
      }
    };

    loadPaymentModes();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const active = masters.academicSessions.find((s) => s.isActive);
    if (active && !sessionId) setSessionId(active.id);
  }, [masters.academicSessions, sessionId]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!classId) {
        setSections([]);
        setSectionId("");
        return;
      }
      const next = await fetchSections(classId);
      if (!cancelled) {
        setSections(Array.isArray(next) ? next : []);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [classId, fetchSections]);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      if (!sessionId) {
        setStudentOptions([]);
        return;
      }
      try {
        const rows = await feesApi.students({
          sessionId,
          classId: classId || undefined,
          sectionId: sectionId || undefined,
          search: search || undefined,
        });
        if (!cancelled) {
          setStudentOptions(Array.isArray(rows) ? rows : []);
        }
      } catch {
        if (!cancelled) setStudentOptions([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [sessionId, classId, sectionId, search]);

  const loadDemand = useCallback(async (studentId: string, selectedSessionId: string) => {
    setLoadingDemand(true);
    try {
      const [data, unifiedSummary] = await Promise.all([
        feesApi.getDemand(studentId, selectedSessionId, paymentDate || undefined),
        feesApi.getSummary(studentId, selectedSessionId),
      ]);
      setDemand(data);
      setSummary(unifiedSummary);
      setPaymentHistory(Array.isArray(data?.recentPayments) ? data.recentPayments : []);
    } catch (e: any) {
      setDemand(null);
      setSummary(null);
      setPaymentHistory([]);
      setError(e?.response?.data?.message || e?.message || "Failed to fetch student demand");
    } finally {
      setLoadingDemand(false);
    }
  }, [paymentDate]);

  const onSelectStudent = async (_: any, value: StudentOption | null) => {
    setStudent(value);
    setResult(null);
    setError(null);
    if (!value || !sessionId) {
      setDemand(null);
      setSummary(null);
      setPaymentHistory([]);
      return;
    }
    await loadDemand(value.studentId, sessionId);
    if (!classId && value.class?.id) setClassId(value.class.id);
    if (!sectionId && value.section?.id) setSectionId(value.section.id);
  };

  useEffect(() => {
    if (!student?.studentId || !sessionId) return;
    loadDemand(student.studentId, sessionId);
  }, [student?.studentId, sessionId, paymentDate, loadDemand]);

  const onDeposit = async () => {
    if (!student) {
      setError("Please select a student");
      return;
    }
    if (!sessionId) {
      setError("Academic Session is required");
      return;
    }
    if (!paidAmount || amount(paidAmount) <= 0) {
      setError("Amount Paid is required");
      return;
    }
    if (!paymentMode) {
      setError("Payment mode is required");
      return;
    }
    if (isReferenceRequired && !referenceNo.trim()) {
      setError(`Reference number is required for ${selectedPaymentMode?.name || "selected payment mode"}`);
      return;
    }
    if (isChequeMode && (!chequeNo || !chequeBank || !chequeDate)) {
      setError("Cheque number, bank name and cheque date are required");
      return;
    }

    setSubmitting(true);
    setError(null);
    setResult(null);

    try {
      const response = await feesApi.deposit({
        studentId: student.studentId,
        sessionId,
        amount: amount(paidAmount),
        paymentDate,
        paymentMode,
        referenceNo: referenceNo || undefined,
        remarks: remarks || undefined,
        chequeNo: isChequeMode ? chequeNo : undefined,
        chequeBank: isChequeMode ? chequeBank : undefined,
        chequeDate: isChequeMode ? chequeDate : undefined,
      });

      setResult(response);
      await loadDemand(student.studentId, sessionId);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Deposit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const openReceiptPreview = async () => {
    const receiptId = result?.receipt?.id;
    if (!receiptId) return;
    try {
      const file = await feesApi.getReceiptPdfBlob(receiptId);
      const url = URL.createObjectURL(file.blob);
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = file.filename;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to open receipt preview");
    }
  };

  const applyDiscount = async () => {
    if (!student?.studentId) {
      setError("Select student before applying discount");
      return;
    }
    if (!discountValue || amount(discountValue) <= 0) {
      setError("Enter valid discount value");
      return;
    }
    setAdjusting(true);
    setError(null);
    try {
      await feesApi.discount({
        studentId: student.studentId,
        sessionId,
        discountType,
        value: amount(discountValue),
        reason: discountReason || undefined,
        applyToAll: discountApplyToAll,
        feeHeadIds: discountApplyToAll ? undefined : discountFeeHeadIds,
      });
      await loadDemand(student.studentId, sessionId);
      setDiscountValue("");
      setDiscountReason("");
      setDiscountFeeHeadIds([]);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to apply discount");
    } finally {
      setAdjusting(false);
    }
  };

  const applyFine = async () => {
    if (!student?.studentId) {
      setError("Select student before applying fine");
      return;
    }
    if (!fineValue || amount(fineValue) <= 0) {
      setError("Enter valid fine value");
      return;
    }
    setAdjusting(true);
    setError(null);
    try {
      await feesApi.fine({
        studentId: student.studentId,
        sessionId,
        fineType,
        value: amount(fineValue),
        reason: fineReason || "Late fee fine",
        applyToAll: fineApplyToAll,
        feeHeadIds: fineApplyToAll ? undefined : fineFeeHeadIds,
      });
      await loadDemand(student.studentId, sessionId);
      setFineValue("");
      setFineReason("");
      setFineFeeHeadIds([]);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to apply fine");
    } finally {
      setAdjusting(false);
    }
  };

  const deleteDiscount = async (id: string) => {
    if (!student?.studentId) return;
    setAdjusting(true);
    setError(null);
    try {
      await feesApi.deleteDiscount(id);
      await loadDemand(student.studentId, sessionId);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to delete discount");
    } finally {
      setAdjusting(false);
    }
  };

  const deleteFine = async (id: string) => {
    if (!student?.studentId) return;
    setAdjusting(true);
    setError(null);
    try {
      await feesApi.deleteFine(id);
      await loadDemand(student.studentId, sessionId);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to delete fine");
    } finally {
      setAdjusting(false);
    }
  };

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
          Fee Deposit
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!canCreate && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You do not have FEES_DEPOSIT_CREATE permission. Deposit submit is disabled.
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Academic Session"
                value={sessionId}
                onChange={(e) => {
                  setSessionId(e.target.value);
                  setStudent(null);
                  setDemand(null);
                  setResult(null);
                }}
                required
              >
                {masters.academicSessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} {s.isActive ? "(Active)" : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Class (Optional)"
                value={classId}
                onChange={(e) => {
                  setClassId(e.target.value);
                  setSectionId("");
                  setStudent(null);
                  setDemand(null);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {masters.classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Section (Optional)"
                value={sectionId}
                onChange={(e) => {
                  setSectionId(e.target.value);
                  setStudent(null);
                  setDemand(null);
                }}
                disabled={!classId}
              >
                <MenuItem value="">All</MenuItem>
                {sections.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Student Selection
          </Typography>

          <Autocomplete
            options={studentOptions}
            value={student}
            onChange={onSelectStudent}
            onInputChange={(_, value) => setSearch(value)}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName} | Scholar: ${option.scholarNumber || "-"} | Adm: ${option.admissionNumber || "-"}`}
            isOptionEqualToValue={(a, b) => a.studentSessionRecordId === b.studentSessionRecordId}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search by Name / Scholar No / Admission No"
                placeholder="Start typing to search"
              />
            )}
          />
        </Paper>

        {student && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Student Info
                  </Typography>
                  <Typography variant="h6">
                    {student.firstName} {student.lastName}
                  </Typography>
                  <Typography variant="body2">Scholar No: {student.scholarNumber || "-"}</Typography>
                  <Typography variant="body2">Admission No: {student.admissionNumber || "-"}</Typography>
                  <Typography variant="body2">
                    {student.class?.name || "-"} - {student.section?.name || "-"}
                  </Typography>
                  <Typography variant="body2">Session: {student.session?.name || "-"}</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Fee Summary
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`Total: ${amount(demand?.totalFee).toFixed(2)}`} color="default" />
                    <Chip label={`Discount: ${amount(summary?.totalDiscount ?? demand?.discount).toFixed(2)}`} color="info" />
                    <Chip label={`Fine: ${amount(summary?.totalPenalty ?? demand?.fine).toFixed(2)}`} color="warning" />
                    <Chip label={`Final Fee: ${amount((summary?.totalFee ?? demand?.totalFee) + (summary?.totalPenalty ?? demand?.fine) - (summary?.totalDiscount ?? demand?.discount)).toFixed(2)}`} color="primary" />
                    <Chip label={`Paid: ${amount(summary?.totalPaid ?? demand?.totalPaid).toFixed(2)}`} color="success" />
                    <Chip label={`Due: ${amount(summary?.finalDue ?? demand?.totalDue).toFixed(2)}`} color="error" />
                    <Chip label={`Due by ${demand?.dueAsOfDate || "date"}: ${amount(demand?.totalDueAsOfDate).toFixed(2)}`} color="secondary" />
                    <Chip
                      label={`Upcoming Due Date: ${demand?.nextDueDate ? new Date(demand.nextDueDate).toLocaleDateString() : "-"}`}
                      color="default"
                    />
                    <Chip label={`Upcoming Due Amount: ${amount(demand?.nextDueAmount).toFixed(2)}`} color="default" />
                  </Stack>
                  {loadingDemand && <Typography sx={{ mt: 1 }}>Refreshing summary...</Typography>}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {demand?.breakdown?.length ? (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Fee Breakdown (Date-wise)
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fee Head</TableCell>
                  <TableCell>Installment</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell align="right">Base Amount</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Fine</TableCell>
                  <TableCell align="right">Adjusted Amount</TableCell>
                  <TableCell align="right">Paid</TableCell>
                  <TableCell align="right">Due</TableCell>
                  <TableCell align="right">Due By Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {demand.breakdown.map((row: any) => (
                  <TableRow key={`${row.feeHeadId}-${row.feeInstallmentId}`}>
                    <TableCell>{row.feeHead}</TableCell>
                    <TableCell>{row.installment}</TableCell>
                    <TableCell>{row.dueDate ? new Date(row.dueDate).toLocaleDateString() : "-"}</TableCell>
                    <TableCell align="right">{amount(row.payable).toFixed(2)}</TableCell>
                    <TableCell align="right">{amount(row.discountApplied).toFixed(2)}</TableCell>
                    <TableCell align="right">{amount(row.fineApplied).toFixed(2)}</TableCell>
                    <TableCell align="right">{amount(row.adjustedPayable).toFixed(2)}</TableCell>
                    <TableCell align="right">{amount(row.paid).toFixed(2)}</TableCell>
                    <TableCell align="right">{amount(row.due).toFixed(2)}</TableCell>
                    <TableCell align="right">{amount(row.dueAsOfDate).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3}>
                    <strong>Total</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{amount(demand.totalFee).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{amount(summary?.totalDiscount ?? demand.discount).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{amount(summary?.totalPenalty ?? demand.fine).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{amount((summary?.totalFee ?? demand.totalFee) + (summary?.totalPenalty ?? demand.fine) - (summary?.totalDiscount ?? demand.discount)).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{amount(summary?.totalPaid ?? demand.totalPaid).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{amount(summary?.finalDue ?? demand.totalDue).toFixed(2)}</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{amount(demand.totalDueAsOfDate).toFixed(2)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Paper>
        ) : null}

        {demand && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Discount & Fine
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <Chip label={`Total Discount: ${amount(summary?.totalDiscount ?? demand.discount).toFixed(2)}`} color="info" />
              <Chip label={`Total Fine: ${amount(summary?.totalPenalty ?? demand.fine).toFixed(2)}`} color="warning" />
            </Stack>

            {Array.isArray(demand.adjustmentDetails) && demand.adjustmentDetails.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Fee Head</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {demand.adjustmentDetails.map((adj: any) => (
                    <TableRow key={adj.id}>
                      <TableCell>{adj.type}</TableCell>
                      <TableCell>{adj.feeHeadName || "All"}</TableCell>
                      <TableCell>{adj.date ? new Date(adj.date).toLocaleDateString() : "-"}</TableCell>
                      <TableCell align="right">{amount(adj.amount).toFixed(2)}</TableCell>
                      <TableCell>{adj.transactionId || "-"}</TableCell>
                      <TableCell>{adj.remarks || "-"}</TableCell>
                      <TableCell>
                        {adj.type === "DISCOUNT" ? (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => deleteDiscount(adj.id)}
                            disabled={adjusting}
                          >
                            Delete
                          </Button>
                        ) : adj.type === "FINE" ? (
                          <Button
                            size="small"
                            color="error"
                            onClick={() => deleteFine(adj.id)}
                            disabled={adjusting}
                          >
                            Delete
                          </Button>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No discount or fine entries available.
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Set Discount % / Late Fine Amount
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Discount Type"
                  value={discountType}
                  onChange={(e) => setDiscountType(e.target.value)}
                >
                  <MenuItem value="PERCENTAGE">PERCENTAGE</MenuItem>
                  <MenuItem value="FIXED">FIXED</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Discount Value"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Discount Reason"
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Discount Scope"
                  value={discountApplyToAll ? "ALL" : "SELECTED"}
                  onChange={(e) => {
                    const all = e.target.value === "ALL";
                    setDiscountApplyToAll(all);
                    if (all) setDiscountFeeHeadIds([]);
                  }}
                >
                  <MenuItem value="ALL">All Fee Heads</MenuItem>
                  <MenuItem value="SELECTED">Selected Fee Heads</MenuItem>
                </TextField>
              </Grid>
              {!discountApplyToAll && (
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    multiple
                    options={feeHeadOptions}
                    value={feeHeadOptions.filter((h) => discountFeeHeadIds.includes(h.id))}
                    onChange={(_, values) => setDiscountFeeHeadIds(values.map((v) => v.id))}
                    getOptionLabel={(o) => o.name}
                    renderInput={(params) => <TextField {...params} label="Discount Fee Heads" />}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={applyDiscount}
                  disabled={adjusting || !student || !sessionId}
                  sx={{ height: "56px" }}
                >
                  Apply Discount
                </Button>
              </Grid>

              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Fine Type"
                  value={fineType}
                  onChange={(e) => setFineType(e.target.value)}
                >
                  <MenuItem value="FIXED">FIXED</MenuItem>
                  <MenuItem value="PERCENTAGE">PERCENTAGE</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Late Fine Value"
                  value={fineValue}
                  onChange={(e) => setFineValue(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Fine Reason"
                  value={fineReason}
                  onChange={(e) => setFineReason(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Fine Scope"
                  value={fineApplyToAll ? "ALL" : "SELECTED"}
                  onChange={(e) => {
                    const all = e.target.value === "ALL";
                    setFineApplyToAll(all);
                    if (all) setFineFeeHeadIds([]);
                  }}
                >
                  <MenuItem value="ALL">All Fee Heads</MenuItem>
                  <MenuItem value="SELECTED">Selected Fee Heads</MenuItem>
                </TextField>
              </Grid>
              {!fineApplyToAll && (
                <Grid item xs={12} md={5}>
                  <Autocomplete
                    multiple
                    options={feeHeadOptions}
                    value={feeHeadOptions.filter((h) => fineFeeHeadIds.includes(h.id))}
                    onChange={(_, values) => setFineFeeHeadIds(values.map((v) => v.id))}
                    getOptionLabel={(o) => o.name}
                    renderInput={(params) => <TextField {...params} label="Fine Fee Heads" />}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  onClick={applyFine}
                  disabled={adjusting || !student || !sessionId}
                  sx={{ height: "56px" }}
                >
                  Apply Fine
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {paymentHistory.length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Paid Fees Details
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt No</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paymentHistory.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.receiptNo}</TableCell>
                    <TableCell>{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString() : "-"}</TableCell>
                    <TableCell>{p.paymentMode || "-"}</TableCell>
                    <TableCell align="right">{amount(p.amount).toFixed(2)}</TableCell>
                    <TableCell>{p.remarks || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Payment Form
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Amount Paid"
                fullWidth
                type="number"
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Payment Date"
                fullWidth
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Payment Mode"
                fullWidth
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
                required
              >
                {paymentModes.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={isReferenceRequired ? "Reference No" : "Reference No (Optional)"}
                fullWidth
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                required={isReferenceRequired}
                helperText={
                  selectedPaymentMode?.isOnline
                    ? "Online payment mode selected"
                    : selectedPaymentMode?.requiresReference
                      ? "Reference required for this payment mode"
                      : ""
                }
              />
            </Grid>

            {isChequeMode && (
              <>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Cheque No"
                    fullWidth
                    value={chequeNo}
                    onChange={(e) => setChequeNo(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Bank Name"
                    fullWidth
                    value={chequeBank}
                    onChange={(e) => setChequeBank(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Cheque Date"
                    fullWidth
                    type="date"
                    value={chequeDate}
                    onChange={(e) => setChequeDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                label="Remarks (Optional)"
                fullWidth
                multiline
                minRows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              disabled={submitting || !canCreate || !student || !sessionId || !paidAmount || !paymentMode}
              onClick={onDeposit}
            >
              {submitting ? "Submitting..." : "Submit Deposit"}
            </Button>
            <Button variant="outlined" disabled={!result?.receipt?.id} onClick={openReceiptPreview}>
              Receipt Preview
            </Button>
            {due > 0 && paidAmount && amount(paidAmount) > due && (
              <Chip color="warning" label={`Overpayment will be stored as advance: ${(amount(paidAmount) - due).toFixed(2)}`} />
            )}
          </Stack>

          {result && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {result.message || "Deposit completed"}
            </Alert>
          )}
        </Paper>
      </Box>
    </MainLayout>
  );
};

export default FeeDepositPage;
